import type { AlbumLengthFormat } from "@/settings/types/types";
import type { AppLanguage } from "@/localization/hooks/use-translation";

/**
 * How long an album runs, written the way the reader asked for it in Settings.
 *
 * It sits beside the preference rather than in the catalog that first needed
 * it: the Settings page labels each format with this function's own output, and
 * a feature reaching into another feature for a pure string helper is the
 * dependency the seams exist to keep out — the desktop puts the same function
 * in `@shared` for that reason.
 *
 * `Intl` owns the unit names of the spelled-out form for the same reason it
 * owns the byte units: "min" is not universal, and a hand-assembled string is a
 * translated sentence built from fragments. Seconds are shown only when there
 * is no minute to show, since nobody reads an album length to the second.
 */
export const formatAlbumLength = ({
  format,
  language,
  seconds,
}: {
  format: AlbumLengthFormat;
  language: AppLanguage;
  seconds: number;
}) => {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;

  if (format === "minutes") {
    return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, "0")}`;
  }

  const unit = (value: number, unitName: "hour" | "minute" | "second") =>
    new Intl.NumberFormat(language, {
      maximumFractionDigits: 0,
      style: "unit",
      unit: unitName,
      unitDisplay: "short",
    }).format(value);

  if (safeSeconds < 60) {
    return unit(safeSeconds, "second");
  }
  // Rounded to the minute before the hours are taken out, so 3599 seconds is an
  // hour rather than "60 min", and an hour on the nose is "1 hr" rather than
  // "1 hr 0 min".
  const roundedMinutes = Math.round(safeSeconds / 60);
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  if (hours === 0) {
    return unit(minutes, "minute");
  }
  return minutes === 0 ? unit(hours, "hour") : `${unit(hours, "hour")} ${unit(minutes, "minute")}`;
};
